import moment from 'moment-timezone';

/**
 * Resolve a usable IANA zone name for register date formatting.
 *
 * `moment(...).tz(name)` is overloaded: with a *falsy* `name` it silently
 * switches from setter to getter and returns `undefined` (or a zone-name
 * string) instead of the moment, so a chained `.format(...)` throws a
 * TypeError. An org can legitimately have `useOrgTimezone === true` while its
 * `timezone` is null/undefined/empty -- exactly that falsy case -- so we only
 * trust the org zone when it is actually set, and otherwise fall back to the
 * browser's guessed zone (and finally UTC). The return is always a non-empty
 * string, which keeps `.tz()` on its setter path.
 */
export function resolveOrgTimezone(useOrgTimezone: boolean, orgTimezone?: string | null): string {
  if (useOrgTimezone && orgTimezone) return orgTimezone;
  return moment.tz.guess() || 'UTC';
}

/**
 * Format a unix-seconds timestamp in the org's preferred timezone. Returns ''
 * for a null/undefined timestamp. The timezone is resolved via
 * `resolveOrgTimezone` so a missing org zone can never crash the caller.
 */
export function formatRegisterDateTime(
  timeSEC: number | null | undefined,
  useOrgTimezone: boolean,
  orgTimezone: string | null | undefined,
  format: string
): string {
  if (timeSEC === null || timeSEC === undefined) return '';
  const tz = resolveOrgTimezone(useOrgTimezone, orgTimezone);
  return moment.unix(timeSEC).tz(tz).format(format);
}
